import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { SsrLocalstoreService } from '../_services/ssr-localstore.service';
import { environment } from 'src/environments/environment';
import { v4 } from 'uuid';

export interface HeaderOptions {
    type: 'auth' | 'basic' | 'nonAuthChat'
    fingerprint?: string
    identifier?: string
    responseTye?: string
}

@Injectable({
    providedIn: 'root'
})
export class bleachrHTTP {
    private defaultHeaders: HeaderOptions = {
        type: 'basic',
        identifier: 'tennis-one'
    }

    constructor(
        private http: HttpClient,
        private ssr: SsrLocalstoreService
    ) { }

    private fingerprint = this.ssr.getItem('tennisOne-fingerprint') ? this.ssr.getItem('tennisOne-fingerprint') as string : `web-${v4()}`

    private basicHeaders(identifier?: string): { headers: HttpHeaders } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                'fingerprint': this.fingerprint
            })
        };
        return httpOptions;
    }

    private authHeaders(identifier?: string, responseType?: string): { headers: HttpHeaders; responseType?: string } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                Authorization: `Token token=${this.ssr.getJWT()}`,
                'fingerprint': this.fingerprint
            }),
            ...(responseType ? { responseType } : {})
        };
        return httpOptions;
    }

    private nonAuthChatHeaders(identifier?: string): { headers: HttpHeaders } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                'fingerprint': this.fingerprint
            })
        };
        return httpOptions;
    }

    async get<request_type>(url: string, options: HeaderOptions = this.defaultHeaders): Promise<request_type> {
        try {
            const header: { headers: HttpHeaders } = options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier)

            const request = this.http.get<request_type>(url, header)

            return await lastValueFrom(request);
        } catch (err) {
            throw err;
        }
    }

    async post<request_type, payload_type>(url: string, payload: payload_type, options: HeaderOptions = this.defaultHeaders): Promise<request_type> {
        try {
            const header = options?.type === 'auth' ? this.authHeaders(options.identifier, options?.responseTye)
                : options?.type === 'nonAuthChat' ? this.nonAuthChatHeaders(options.identifier)
                    : this.basicHeaders(options.identifier)

            const request = this.http.post<request_type>(url, payload, header)

            return await lastValueFrom(request);
        } catch (err) {
            throw err;
        }
    }

    async patch<request_type, payload_type>(url: string, payload: payload_type, options: HeaderOptions = this.defaultHeaders): Promise<request_type> {
        try {
            const header = options?.type === 'auth' ? this.authHeaders(options.identifier, options?.responseTye)
                : options?.type === 'nonAuthChat' ? this.nonAuthChatHeaders(options.identifier)
                    : this.basicHeaders(options.identifier)

            const request = this.http.patch<request_type>(url, payload, header)

            return await lastValueFrom(request);
        } catch (err) {
            throw err;
        }
    }

    async put<request_type, payload_type>(url: string, payload: payload_type, options: HeaderOptions = this.defaultHeaders): Promise<request_type> {
        try {
            const header: { headers: HttpHeaders } = options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier)

            const request = this.http.put<request_type>(url, payload, header)

            return await lastValueFrom(request);
        } catch (err) {
            throw err;
        }
    }

    async delete<request_type>(url: string, options: HeaderOptions = this.defaultHeaders, payload?: any): Promise<request_type> {
        try {
            const header: { headers: HttpHeaders, body?: any } = options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier);

            if (payload) header.body = payload;

            const request = this.http.delete<request_type>(url, header)

            return await lastValueFrom(request);
        } catch (err) {
            throw err;
        }
    }

}